import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  CreditCard, Loader2, CheckCircle2, XCircle, AlertTriangle, Filter,
  Euro, RefreshCcw, Bell, RotateCcw,
} from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(_locale(), {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtEur(n, currency = "EUR") {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat(_locale(), {
      style: "currency", currency: (currency || "eur").toUpperCase(),
    }).format(n);
  } catch { return `${n} ${currency?.toUpperCase() || ""}`; }
}

const SERVICE_LABEL = {
  miab_launch: _t("MIAB — pubblicazione"),
  racconti_entry: _t("Racconti — quota autore"),
};

const STATUS_STYLE = {
  paid: "text-emerald-400 border-emerald-400/30 bg-emerald-500/10",
  pending: "text-amber-400 border-amber-400/30 bg-amber-500/10",
  failed: "text-red-400 border-red-400/30 bg-red-500/10",
  expired: "text-red-400 border-red-400/30 bg-red-500/10",
};

export default function AdminPayments() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterService, setFilterService] = useState("");
  const [retrying, setRetrying] = useState(null); // session_id currently retrying

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterStatus) q.set("status", filterStatus);
      if (filterService) q.set("service", filterService);
      const [txRes, evRes] = await Promise.all([
        api.get(`/admin/payments/transactions?${q.toString()}`),
        api.get("/admin/payments/webhook-events?limit=30"),
      ]);
      setData(txRes.data);
      setEvents(evRes.data.events || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.is_admin) load(); /* eslint-disable-next-line */ }, [user, filterStatus, filterService]);

  const retryActivation = async (sessionId) => {
    setRetrying(sessionId);
    try {
      const { data: r } = await api.post(`/admin/payments/transactions/${sessionId}/retry-activation`);
      if (r.outcome === "ok") {
        toast.success(_t("Attivazione riuscita."));
      } else {
        toast.error(_t("Retry fallito: {0}", r.error || "errore sconosciuto"));
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetrying(null);
    }
  };

  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <AppLayout>
      <AdminTabs current="payments" />

      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Admin · Pagamenti")}</div>
          <h1 className="font-display text-5xl text-white leading-tight mb-2">{_t("Transazioni Stripe")}</h1>
          <p className="text-white/60 max-w-2xl">{_t("Solo pagamenti verso Time Capsule (MIAB, Racconti). Il modulo voucher / trasferimento di denaro nel tempo resta WIP e non è mostrato qui. Stripe opera in")}{" "}<span className="text-amber-400 font-medium">{_t("TEST MODE")}</span>.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-ghost !text-sm"
          data-testid="admin-payments-refresh"
        >{_t("{0} Aggiorna", loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />)}</button>
      </div>

      {loading || !data ? (
        <Loader2 className="w-8 h-8 text-[#FF5722] animate-spin mx-auto my-16" />
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <SummaryCard icon={<CreditCard className="w-4 h-4" />} label={_t("Sessioni totali")}
                         value={data.totals.count} testId="totals-count" />
            <SummaryCard icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} label={_t("Confermate")}
                         value={data.totals.paid_count} testId="totals-paid" />
            <SummaryCard icon={<AlertTriangle className="w-4 h-4 text-amber-400" />} label={_t("In attesa")}
                         value={data.totals.pending_count} testId="totals-pending" />
            <SummaryCard icon={<Euro className="w-4 h-4 text-emerald-400" />} label={_t("Incassato")}
                         value={fmtEur(data.totals.amount_paid_eur)} testId="totals-amount" />
          </div>

          {data.totals.activation_error_count > 0 && (
            <div className="tc-card p-4 mb-6" style={{ borderColor: _t("rgba(239,68,68,0.35)"), background: _t("rgba(239,68,68,0.06)") }}>
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <div className="text-sm">
                  <span className="text-red-400 font-medium">{_t("{0} attivazione/i con errore", data.totals.activation_error_count)}</span>
                  <span className="text-white/60">{" "}{_t("— il pagamento è stato confermato ma l'attivazione del servizio ha fallito. Richiedono revisione.")}</span>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <Filter className="w-4 h-4 text-white/50" />
            <select data-testid="filter-status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-white/5 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white">
              <option value="">{_t("Tutti gli stati")}</option>
              <option value="paid">{_t("Confermati")}</option>
              <option value="pending">{_t("In attesa")}</option>
              <option value="failed">{_t("Falliti")}</option>
              <option value="expired">{_t("Scaduti")}</option>
            </select>
            <select data-testid="filter-service" value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="bg-white/5 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white">
              <option value="">{_t("Tutti i servizi")}</option>
              <option value="miab_launch">{_t("MIAB")}</option>
              <option value="racconti_entry">{_t("Racconti")}</option>
            </select>
          </div>

          <div className="tc-card p-0 overflow-hidden mb-10">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-payments-table">
                <thead className="bg-white/5 text-white/70">
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">{_t("Data")}</th>
                    <th className="text-left px-3 py-2 font-normal">{_t("Utente")}</th>
                    <th className="text-left px-3 py-2 font-normal">{_t("Servizio")}</th>
                    <th className="text-right px-3 py-2 font-normal">{_t("Importo")}</th>
                    <th className="text-left px-3 py-2 font-normal">{_t("Stato")}</th>
                    <th className="text-left px-3 py-2 font-normal">{_t("Attivazione")}</th>
                    <th className="text-left px-3 py-2 font-normal">{_t("Notifica")}</th>
                    <th className="text-left px-3 py-2 font-normal">{_t("Session / Intent")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-white/50">{_t("Nessuna transazione")}</td></tr>
                  )}
                  {data.transactions.map((t) => (
                    <tr key={t.session_id} className="border-t border-white/5"
                        data-testid={`tx-row-${t.session_id.slice(-8)}`}>
                      <td className="px-3 py-2 text-white/60 whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                      <td className="px-3 py-2 text-white">
                        <div className="font-medium">{t.user.username || "—"}</div>
                        <div className="text-white/40 text-xs">{t.user.email}</div>
                      </td>
                      <td className="px-3 py-2 text-white/80">{SERVICE_LABEL[t.service] || t.service}</td>
                      <td className="px-3 py-2 text-right text-white font-medium">
                        {fmtEur(t.amount_eur, t.currency)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-xs ${STATUS_STYLE[t.payment_status] || "text-white/60 border-white/20"}`}>
                          {t.payment_status}
                        </span>
                        {t.paid_at && (
                          <div className="text-white/40 text-[10px] mt-1">{fmtDateTime(t.paid_at)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {t.activation_error ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 text-red-400 text-xs" title={t.activation_error}>
                              <XCircle className="w-3.5 h-3.5" />{" "}{_t("Errore")}</span>
                            <button
                              data-testid={`retry-activation-${t.session_id.slice(-8)}`}
                              onClick={() => retryActivation(t.session_id)}
                              disabled={retrying === t.session_id}
                              className="btn-ghost !text-[11px] !px-2 !py-1 self-start"
                              title={_t("Riprova l'attivazione del servizio (idempotente, non genera un nuovo pagamento)")}
                            >
                              {retrying === t.session_id
                                ? <><Loader2 className="w-3 h-3 animate-spin" />{" "}{_t("Attendere…")}</>
                                : <><RotateCcw className="w-3 h-3" />{" "}{_t("Riprova attivazione")}</>}
                            </button>
                            {t.retry_attempts && t.retry_attempts.length > 0 && (
                              <div className="text-[10px] text-white/40">{_t("{0} tentativi", t.retry_attempts.length)}</div>
                            )}
                          </div>
                        ) : t.service_activated ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" />{" "}{_t("Attivato")}{t.retry_attempts && t.retry_attempts.length > 0 && (
                              <span className="text-white/40 text-[10px] ml-1">{_t("(dopo {0} retry)", t.retry_attempts.length)}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-white/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {t.notified_user ? (
                          <span className="inline-flex items-center gap-1 text-cyan-400 text-xs">
                            <Bell className="w-3.5 h-3.5" />{" "}{_t("Inviata")}</span>
                        ) : (
                          <span className="text-white/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-white/50 font-mono-launch text-xs">
                        <div>{t.session_id.slice(0, 22)}…</div>
                        {t.payment_intent && <div className="text-white/40">{t.payment_intent.slice(0, 22)}…</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-display text-2xl text-white mb-3">{_t("Eventi webhook Stripe")}</h2>
            <p className="text-white/50 text-sm mb-4">{_t("Log di idempotenza: gli event_id già processati non riattivano il servizio.")}</p>
            <div className="tc-card p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-webhook-events-table">
                  <thead className="bg-white/5 text-white/70">
                    <tr>
                      <th className="text-left px-3 py-2 font-normal">{_t("Ricevuto")}</th>
                      <th className="text-left px-3 py-2 font-normal">{_t("Tipo evento")}</th>
                      <th className="text-left px-3 py-2 font-normal">{_t("Session")}</th>
                      <th className="text-left px-3 py-2 font-normal">{_t("Event ID")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-8 text-white/50">{_t("Nessun evento webhook ricevuto ancora")}</td></tr>
                    )}
                    {events.map((e) => (
                      <tr key={e.event_id} className="border-t border-white/5">
                        <td className="px-3 py-2 text-white/60 whitespace-nowrap">{fmtDateTime(e.received_at)}</td>
                        <td className="px-3 py-2 text-white/80">{e.event_type}</td>
                        <td className="px-3 py-2 text-white/50 font-mono-launch text-xs">{e.session_id ? e.session_id.slice(0, 22) + "…" : "—"}</td>
                        <td className="px-3 py-2 text-white/40 font-mono-launch text-xs">{e.event_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}

function SummaryCard({ icon, label, value, testId }) {
  return (
    <div className="tc-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 flex items-center gap-2">
        {icon} {label}
      </div>
      <div className="font-display text-3xl text-white">{value}</div>
    </div>
  );
}

function AdminTabs({ current }) {
  const tabs = [
    { key: "agreements", href: "/admin/convenzioni", label: _t("Convenzioni") },
    { key: "consent", href: "/admin/consensi", label: _t("Consensi") },
    { key: "payments", href: "/admin/pagamenti", label: _t("Pagamenti") },
    { key: "users", href: "/admin/utenti", label: _t("Utenti") },
  ];
  return (
    <div className="mb-6 flex gap-2 border-b border-white/10">
      {tabs.map((t) => (
        <a key={t.key} href={t.href}
           className={`px-4 py-3 -mb-px text-sm border-b-2 transition-colors ${current === t.key ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60 hover:text-white"}`}
           data-testid={`tab-admin-${t.key}`}>
          {t.label}
        </a>
      ))}
    </div>
  );
}
